import { parseChordPro } from './parser';
import { parseStructuredSong } from './structuredSongAdapter';

export interface SongIntegrations {
  songselect_id?: string;
  publisher?: string;
  license?: string;
  source?: string;
  google_drive_file_id?: string;
  google_drive_folder_id?: string;
  pdf_link_url?: string;
  pdf_link_label?: string;
}

export interface SongImportMetadata {
  source_type?: string;
  source_file_name?: string;
  source_file_url?: string;
  source_file_id?: string;
  source_google_drive_file_id?: string;
  original_extracted_text?: string;
  import_status?: string;
  import_warnings?: string[];
  confidence_score?: number;
}

const optional = (value: string | undefined): string | null => value?.trim() || null;

export const buildSongPayload = (
  source: string,
  integrations: SongIntegrations = {},
  importMetadata: SongImportMetadata = {},
): Record<string, unknown> => {
  const parsed = parseChordPro(source);
  const meta = parsed.metadata;
  const tempo = Number.parseInt(meta.tempo || '', 10);
  const capo = Number.parseInt(meta.capo || '', 10);
  const year = Number.parseInt(meta.year || '', 10);
  return {
    title: meta.title?.trim() || 'Naamloos lied',
    subtitle: optional(meta.subtitle), artist: optional(meta.artist), composer: optional(meta.composer),
    lyricist: optional(meta.lyricist), copyright: optional(meta.copyright),
    publisher: optional(integrations.publisher || meta.publisher),
    license: optional(integrations.license || meta.license), source: optional(integrations.source || meta.source),
    album: optional(meta.album), year: Number.isFinite(year) ? year : null,
    language: optional(meta.language), default_key: optional(meta.key), alternative_keys: [],
    bpm: Number.isFinite(tempo) && tempo >= 20 && tempo <= 300 ? tempo : null,
    time_signature: optional(meta.time), capo: Number.isFinite(capo) ? capo : null,
    duration: optional(meta.duration),
    tags: (meta.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean),
    ccli_number: optional(meta.ccli), songselect_id: optional(integrations.songselect_id),
    youtube_url: optional(meta['x-zoea-youtube'] || meta.youtube),
    spotify_url: optional(meta['x-zoea-spotify'] || meta.spotify),
    google_drive_file_id: optional(integrations.google_drive_file_id || meta['x-zoea-drive']),
    google_drive_folder_id: optional(integrations.google_drive_folder_id),
    pdf_link_url: optional(integrations.pdf_link_url),
    pdf_link_label: optional(integrations.pdf_link_label),
    chordpro: source, lyrics: parsed.lyrics || null, notes: null,
    structured_song: parseStructuredSong(source).song,
    zoea_metadata: Object.fromEntries(Object.entries(meta).filter(([key, value]) => key.startsWith('x-zoea-') && value)),
    unknown_directives: parsed.directives.filter((directive) => !directive.known).map((directive) => directive.raw),
    source_type: importMetadata.source_type || null,
    source_file_name: importMetadata.source_file_name || null,
    source_file_url: importMetadata.source_file_url || null,
    source_file_id: importMetadata.source_file_id || null,
    source_google_drive_file_id: importMetadata.source_google_drive_file_id || null,
    original_extracted_text: importMetadata.original_extracted_text || null,
    import_status: importMetadata.import_status || null,
    import_warnings: importMetadata.import_warnings || [],
    confidence_score: importMetadata.confidence_score ?? null,
  };
};
